import { useState } from 'react';
import {
  useUpdateBondMutation,
  useUpdateBondWorkflowMutation,
} from 'redux/features/employeeBondApi';
import { toast } from 'react-toastify';
import dayjs from 'lib/dayjs';
import Icon from 'components/AppIcon';

const WORKFLOW_LABELS = {
  UPCOMING: 'Upcoming',
  IN_PROGRESS: 'In Progress',
  ON_HOLD: 'On Hold',
  DONE: 'Done',
};

const STATUS_LABELS = {
  PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700' },
  EXPIRED: { label: 'Expired', color: 'bg-red-100 text-red-700' },
  COMPLETED: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  BREACHED: { label: 'Breached', color: 'bg-rose-100 text-rose-700' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700' },
};

const VALID_TRANSITIONS = {
  UPCOMING: ['IN_PROGRESS'],
  IN_PROGRESS: ['ON_HOLD', 'DONE'],
  ON_HOLD: ['IN_PROGRESS'],
  DONE: [],
};

const getDaysRemaining = (endDate) => {
  if (!endDate) return null;
  return dayjs(endDate).diff(dayjs(), 'day');
};

const getPriorityInfo = (days) => {
  if (days === null) return null;
  if (days <= 0) return { label: 'Overdue', color: 'text-red-600 font-semibold' };
  if (days <= 7) return { label: `${days} days left`, color: 'text-red-600 font-semibold' };
  if (days <= 30) return { label: `${days} days left`, color: 'text-amber-600 font-semibold' };
  return { label: `${days} days left`, color: 'text-gray-600' };
};

const BondDetailModal = ({ isOpen, onClose, bond, onRenew }) => {
  const [updateWorkflow, { isLoading: isUpdating }] = useUpdateBondWorkflowMutation();
  const [updateBond, { isLoading: isStatusUpdating }] = useUpdateBondMutation();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedBondStatus, setSelectedBondStatus] = useState('');

  if (!isOpen || !bond) return null;

  const currentStatus = bond.workflow_status;
  const allowed = VALID_TRANSITIONS[currentStatus] || [];
  const statusBadge = STATUS_LABELS[bond.status] || {
    label: bond.status,
    color: 'bg-gray-100 text-gray-700',
  };
  const days = getDaysRemaining(bond.end_date);
  const priority = getPriorityInfo(days);
  const userName = bond.user ? `${bond.user.first_name} ${bond.user.last_name}` : 'N/A';
  const deptName = bond.user?.department?.name || 'N/A';
  const isDone = currentStatus === 'DONE';

  const handleWorkflowChange = async () => {
    if (!selectedStatus || selectedStatus === currentStatus) return;
    try {
      await updateWorkflow({ id: bond.id, workflow_status: selectedStatus }).unwrap();
      toast.success(`Moved to ${WORKFLOW_LABELS[selectedStatus] || selectedStatus}`);
      onClose();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update status');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-2xl bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-bold text-primary-700">
                {bond.user?.first_name?.[0]}
                {bond.user?.last_name?.[0]}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{userName}</h3>
              <p className="text-xs text-gray-500">
                {bond.user?.employee_id} · {deptName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Workflow Status */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Current Column:</span>
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                currentStatus === 'UPCOMING'
                  ? 'bg-amber-100 text-amber-700'
                  : currentStatus === 'IN_PROGRESS'
                    ? 'bg-blue-100 text-blue-700'
                    : currentStatus === 'ON_HOLD'
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              {WORKFLOW_LABELS[currentStatus] || currentStatus}
            </span>
          </div>

          {/* Bond Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Start Date
              </p>
              <p className="text-sm font-medium text-gray-900">
                {dayjs(bond.start_date).format('DD MMM YYYY')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                End Date
              </p>
              <p className="text-sm font-medium text-gray-900">
                {bond.end_date ? dayjs(bond.end_date).format('DD MMM YYYY') : '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Signed Date
              </p>
              <p className="text-sm font-medium text-gray-900">
                {bond.signed_date ? dayjs(bond.signed_date).format('DD MMM YYYY') : '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Bond Status
              </p>
              <span
                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}
              >
                {statusBadge.label}
              </span>
            </div>
          </div>

          {/* Days Remaining / Priority */}
          {days !== null && (
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
              <Icon
                name="Clock"
                size={16}
                className={days <= 0 ? 'text-red-500' : 'text-gray-500'}
              />
              <span className={priority?.color}>{priority?.label}</span>
            </div>
          )}

          {/* Renewal Info */}
          {bond.is_renew && (
            <div className="flex items-center gap-2 bg-blue-50 rounded-xl px-4 py-3 border border-blue-200">
              <Icon name="RefreshCw" size={16} className="text-blue-500" />
              <span className="text-sm text-blue-700 font-medium">This is a renewed bond</span>
            </div>
          )}

          {/* Workflow Status Change */}
          {!isDone && allowed.length > 0 && (
            <div className="border-t border-gray-200 pt-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Move to column
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">— Select —</option>
                  {allowed.map((s) => (
                    <option key={s} value={s}>
                      {WORKFLOW_LABELS[s] || s}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleWorkflowChange}
                  disabled={!selectedStatus || isUpdating}
                  className="px-4 py-2 bg-primary hover:bg-primary-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isUpdating ? (
                    <Icon name="Loader" size={14} className="animate-spin" />
                  ) : (
                    <Icon name="ArrowRight" size={14} />
                  )}
                  Move
                </button>
              </div>
            </div>
          )}

          {/* Bond Status Change */}
          {!isDone && (
            <div className="border-t border-gray-200 pt-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Bond Status</label>
              <div className="flex items-center gap-3">
                <select
                  value={selectedBondStatus}
                  onChange={(e) => setSelectedBondStatus(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                >
                  <option value="">
                    — Current: {STATUS_LABELS[bond.status]?.label || bond.status} —
                  </option>
                  {Object.entries(STATUS_LABELS).map(([key, val]) => (
                    <option key={key} value={key}>
                      {val.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={async () => {
                    if (!selectedBondStatus) return;
                    try {
                      await updateBond({ id: bond.id, status: selectedBondStatus }).unwrap();
                      toast.success(
                        `Status changed to ${STATUS_LABELS[selectedBondStatus]?.label}`,
                      );
                      onClose();
                    } catch (err) {
                      toast.error(err?.data?.message || 'Failed to update bond status');
                    }
                  }}
                  disabled={!selectedBondStatus || isStatusUpdating}
                  className="px-4 py-2 bg-primary hover:bg-primary-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isStatusUpdating ? (
                    <Icon name="Loader" size={14} className="animate-spin" />
                  ) : (
                    <Icon name="Check" size={14} />
                  )}
                  Update Status
                </button>
              </div>
            </div>
          )}

          {isDone && (
            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-500 italic">
                This bond is already completed. No further moves allowed.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50/80 border-t border-gray-200">
          {!isDone && (
            <button
              onClick={() => {
                onClose();
                if (onRenew) onRenew(bond);
              }}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={14} />
              Renew Bond
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BondDetailModal;
